// Pure logic for bulk task edits (spec 2026-10-27 §B): working-day date shifting, the PUT body
// each selected task gets, and a small concurrency-limited runner. Shared by every page that
// shows an Objectives & Tasks list.
import { addWorkingDays } from "../../api/src/model/template-import.js";

// Move a date by n working days (Mon–Fri, holidays ignored — same rule as template import).
// A weekend date first snaps to the following Monday. Blank stays blank.
export function shiftWorkingDays(dateStr, n) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  const dow = d.getDay();
  if (dow === 6) d.setDate(d.getDate() + 2);
  else if (dow === 0) d.setDate(d.getDate() + 1);
  const snapped = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return addWorkingDays(snapped, n);
}

// The update body one task gets for a bulk action. Only keys the action sets are included.
export function bulkFields(task, action) {
  switch (action.kind) {
    case "assignee": return { assignee: action.assignee };
    case "status": return { status: action.status };
    case "setDates": {
      const out = {};
      if ("startDate" in action) out.startDate = action.startDate;
      if ("dueDate" in action) out.dueDate = action.dueDate;
      return out;
    }
    case "shiftDates": return { startDate: shiftWorkingDays(task.startDate, action.days), dueDate: shiftWorkingDays(task.dueDate, action.days) };
    case "private": return { private: action.value };
    case "move": return { objectiveId: action.objectiveId, projectId: action.projectId ?? null };
    default: throw new Error(`unknown bulk action: ${action.kind}`);
  }
}

// Run fn over items, at most `limit` at once. A result with ok === false, or a throw, is a failure.
export async function runLimited(items, fn, limit = 4) {
  const ok = [];
  const failed = [];
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      try {
        const result = await fn(item);
        if (result && result.ok === false) failed.push({ item, result });
        else ok.push({ item, result });
      } catch (error) {
        failed.push({ item, error });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return { ok, failed };
}
